import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, ViewChild } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { GameType, GetUserCollectionActionsResponse, UndoUserCollectionActionsRequest, UserCollectionAction } from "@apiserver-rest/ponder-data-client-base";
import { AuthService } from "@ponder-core/auth/auth.service";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { addSegmentToList, initializeUserSegmentArray } from "@ponder-features/header/navbar/navbar.component";
import { LoadingComponent } from "@ponder-shared/loading/loading.component";
import { Subscription, take } from "rxjs";
import { UserCollectionHistoryRowComponent } from "./collection/user-collection-history-row.component";
import { ActivatedRoute } from "@angular/router";
import { getHistoryRoute } from "./history-router";
import { SvgIcon, SvgIconMap } from "@ponder-shared/utils/svgIcons";
import { addDays, formatDate } from "@ponder-shared/utils/general";
import { UserCollectionActionToggledEvent } from "@ponder-core/events/collection.events";
import { AuthSession } from "@ponder-core/auth/auth-session";
import { UnauthorizedComponent } from "@ponder-shared/components/unauthorized.component";
import { NavigationService } from "@ponder-core/navigation/navigation.service";
import { NgClass } from "@angular/common";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";

@Component({
    imports: [LoadingComponent, UserCollectionHistoryRowComponent, UnauthorizedComponent, NgClass, ReactiveFormsModule],
    templateUrl: "./user-history.component.html"
})
export class UserHistoryComponent {
    public searchForm!: FormGroup;

    public username?: string;
    public isLoading: boolean = false;
    public loadingSuccess: boolean = false;
    public unauthorized?: boolean = undefined;

    public total: number = 0;
    public start: number = 0;
    public end: number = 0;
    public count: number = 0;
    public actions: UserCollectionAction[] = [];

    public readonly searchSvgIcon: string = SvgIconMap.get(SvgIcon.Search)!;

    private queryGame: GameType = GameType.Magic;
    private queryStart: number = 0;
    private queryCount: number = 50;
    private queryStartDate?: Date;
    private queryEndDate?: Date;
    private queryItemName?: string;
    private queryExactName?: boolean;

    private userActionToggledSub!: Subscription;
    private routeParamsSub!: Subscription;
    private selectedUserActions: UserCollectionAction[] = [];

    constructor (
            formBuilder: FormBuilder,
            private ponderDataClient: PonderDataClient, 
            private events: EventBroker, 
            private auth: AuthService, 
            private pageTitle: Title, 
            private route: ActivatedRoute,
            private navigation: NavigationService) {
        this.searchForm = formBuilder.group({
            name: [""],
            exactName: [""],
            startDate: [""],
            endDate: [""]
        });

        this.userActionToggledSub = this.events.collection.toggledUserCollectionActionEvent.subscribe({
            next: (event: UserCollectionActionToggledEvent) => {
                this.onUserActionToggled(event.action, event.selected);
            }
        });

        this.routeParamsSub = this.route.queryParamMap.subscribe(() => {
            this.fetchData();
        });
    }

    public ngOnDestroy(): void {
        this.userActionToggledSub.unsubscribe();
        this.routeParamsSub?.unsubscribe();
    }

    public ngAfterViewInit(): void {
        this.auth.getSessionAsync().then((authSession?: AuthSession) => {
            if (authSession?.username) {
                this.unauthorized = false;
                this.username = authSession.username;
                this.pageTitle.setTitle(`Ponder > ${this.username} > History`);

                this.sendNavbarUpdate();
                this.fetchData();
            } else {
                this.unauthorized = true;
            }
        });
    }

    private fetchData(): void {
        if (!this.username || this.isLoading) {
            return;
        }

        this.isLoading = true;
        this.setQueryParams();

        this.ponderDataClient
            .userActionsCollection(
                this.queryGame, 
                this.queryStart, 
                this.queryCount,
                // This is odd, I know. I really don't like that JS dates have 0-index for months and days.
                // So we add a day to the query string when we resolve it, so it displays correctly, but then
                // that increment needs to be undone before we send it off. I _think_ the swagger also adds a
                // a day to account for the JS weirdness, so it ends up being 1 too many days. *shrug*
                this.queryStartDate,
                this.queryEndDate,
                this.queryItemName,
                this.queryExactName)
            .pipe(take(1))
            .subscribe({
                next: (response: GetUserCollectionActionsResponse) => {
                    if (response) {
                        this.loadingSuccess = true;
                        this.total = response.total ?? 0;
                        this.count = response.count ?? 0;
                        this.actions = response.actions ?? [];

                        if (response.start != undefined) {
                            this.start = response.start + 1;
                            this.end = response.start + this.count;
                        }
                    }
                }
            })
            .add(() => { this.isLoading = false; });
    }

    private sendNavbarUpdate(): void {
        var segments = initializeUserSegmentArray(this.username!);
        addSegmentToList(segments, "History", "History");
        
        this.events.ui.navbarUpdateEvent.emit(segments);
    }

    public getPrevAction(currIndex: number): UserCollectionAction | undefined {
        if (currIndex == 0) {
            return undefined;
        } else {
            return this.actions[currIndex - 1];
        }
    }

    public static QueryParamGame = "game";
    public static QueryParamStart = "start";
    public static QueryParamStartDate = "startDate";
    public static QueryParamEndDate = "endDate";
    public static QueryParamName = "name";
    public static QueryParamExactName = "exactName";

    private setQueryParams(): void {
        var queryParams = this.route.snapshot.queryParamMap;

        console.debug(JSON.stringify(queryParams));

        this.queryGame = queryParams.has(UserHistoryComponent.QueryParamGame) ? queryParams.get(UserHistoryComponent.QueryParamGame) as GameType : GameType.Magic;
        this.queryStart = queryParams.has(UserHistoryComponent.QueryParamStart) ? parseInt(queryParams.get(UserHistoryComponent.QueryParamStart)!) : 0;
        this.queryStartDate = queryParams.has(UserHistoryComponent.QueryParamStartDate) ? new Date(queryParams.get(UserHistoryComponent.QueryParamStartDate)!) : undefined;
        this.queryEndDate = queryParams.has(UserHistoryComponent.QueryParamEndDate) ? new Date(queryParams.get(UserHistoryComponent.QueryParamEndDate)!) : undefined;
        this.queryItemName = queryParams.get(UserHistoryComponent.QueryParamName) ?? undefined;
        this.queryExactName = queryParams.get(UserHistoryComponent.QueryParamExactName) == "true";

        if (this.queryItemName) {
            this.searchForm.get("name")!.setValue(this.queryItemName);
        } else {
            this.searchForm.get("name")!.setValue("");
        }

        if (this.queryExactName) {
            this.searchForm.get("exactName")!.setValue(true);
        } else {
            this.searchForm.get("exactName")!.setValue(false);
        }

        if (this.queryStartDate) {
            this.searchForm.get("startDate")!.setValue(formatDate(this.queryStartDate));
        } else {
            this.searchForm.get("startDate")!.setValue("");
        }

        if (this.queryEndDate) {
            this.searchForm.get("endDate")!.setValue(formatDate(this.queryEndDate));
        } else {
            this.searchForm.get("endDate")!.setValue("");
        }
    }

    // -------------------------------------------------------------------------------------
    // Actions
    // -------------------------------------------------------------------------------------

    public onPrevClicked(): void {
        if (!this.hasNewer()) {
            return;
        }

        this.navigation.goto("/history", { 
            game: this.queryGame, 
            start: Math.max(0, this.start - 51), 
            startDate: this.queryStartDate, 
            endDate: this.queryEndDate, 
            name: this.queryItemName, 
            exactName: this.queryExactName 
        });
    }

    public onNextClicked(): void {
        if (!this.hasOlder()) {
            return;
        }

        this.navigation.goto("/history", { 
            game: this.queryGame, 
            start: this.end, 
            startDate: this.queryStartDate, 
            endDate: this.queryEndDate, 
            name: this.queryItemName, 
            exactName: this.queryExactName 
        });
      }

    public onSearchClicked(): void {
        var startDateQuery: Date | undefined = undefined;
        var endDateQuery: Date | undefined = undefined;
        var nameQuery: string | undefined = undefined;
        var exactNameQuery: boolean | undefined = undefined;

        if (this.searchForm.get("startDate")!.value.length > 0) {
            startDateQuery = this.searchForm.get("startDate")!.value;
        }

        if (this.searchForm.get("endDate")!.value.length > 0) {
            endDateQuery = this.searchForm.get("endDate")!.value;
        }

        if (this.searchForm.get("name")!.value.length > 0) {
            nameQuery = this.searchForm.get("name")!.value;
        }

        exactNameQuery = this.searchForm.get("exactName")!.value;
        
        this.navigation.goto("/history", { 
            game: this.queryGame, 
            start: 0, 
            startDate: startDateQuery, 
            endDate: endDateQuery, 
            name: nameQuery, 
            exactName: exactNameQuery
        });
    }
    
    // -------------------------------------------------------------------------------------
    // General
    // -------------------------------------------------------------------------------------

    public hasNewer(): boolean {
        return (this.start > 1);
    }

    public hasOlder(): boolean {
        return (this.end < this.total);
    }
    
    // -------------------------------------------------------------------------------------
    // Undo Actions
    // -------------------------------------------------------------------------------------

    private onUserActionToggled(action: UserCollectionAction, selected: boolean): void {
        if (selected) {
            this.selectedUserActions.push(action);
        } else {
            var index = this.selectedUserActions.findIndex(a => a.actionId == action.actionId);

            if (index >= 0) {
                this.selectedUserActions.splice(index, 1);
            }
        }
    }

    public areActionsSelected(): boolean {
        return this.selectedUserActions.length > 0;
    }

    public onClickUndoSelected(): void {
        if (this.selectedUserActions.length == 0) {
            return;
        }

        var request = new UndoUserCollectionActionsRequest({
            actions: this.selectedUserActions
        });

        this.ponderDataClient
            .userActionsCollectionUndo(request)
            .pipe(take(1))
            .subscribe()
            .add(() => { this.navigation.reload() });
    }
}